import { useEffect } from 'react';

import useAccountQuery from 'api/queries/useAccountQuery';
import useFeatureRecordQuery, { FEATURE_KEYS, shouldSeeFeature } from 'api/queries/useFeatureRecordQuery';
import useSubscriptionQuery from 'api/queries/useSubscriptionQuery';
import useMarkFeatureSeen from 'hooks/useMarkFeatureSeen';
import { isWithinNewUserSignupWindow } from 'utils/dateUtils';

/**
 * Custom hook to manage the display logic for onboarding modals.
 *
 * This hook determines whether to show closed or open window onboarding modals
 * based on the marketplace state and feature record. The backend ensures modals
 * are only shown once per user.
 *
 * @param {Object} params
 * @param {Function} params.setClosedWindowOnboardingModalOpen - Setter to control the visibility of the closed window onboarding modal.
 * @param {Function} params.setOpenWindowOnboardingModalOpen - Setter to control the visibility of the open window onboarding modal.
 *
 * @returns {object}
 */
function useOnboardingModals({ setClosedWindowOnboardingModalOpen, setOpenWindowOnboardingModalOpen }) {
    const { data: account } = useAccountQuery();
    const { data: subscription, isLoading: isSubscriptionLoading } = useSubscriptionQuery();
    const { data: featureRecord, isLoading: isFeatureRecordLoading } = useFeatureRecordQuery();
    const { markAsSeen } = useMarkFeatureSeen();

    const marketplaceOpen = account?.marketplaceOpen;
    const onboardingEligible = isWithinNewUserSignupWindow({
        numOrders: account?.numOrders,
        signupDate: subscription?.createdAt?.date,
    });

    const shouldShowOpenWindowOnboarding = () =>
        shouldSeeFeature(featureRecord, FEATURE_KEYS.ONBOARDING_OPEN_MODAL_VIEWED);

    const shouldShowClosedWindowOnboarding = () =>
        shouldSeeFeature(featureRecord, FEATURE_KEYS.ONBOARDING_CLOSED_MODAL_VIEWED);

    const markOpenWindowOnboardingShown = () => {
        markAsSeen(FEATURE_KEYS.ONBOARDING_OPEN_MODAL_VIEWED);
    };

    const markClosedWindowOnboardingShown = () => {
        markAsSeen(FEATURE_KEYS.ONBOARDING_CLOSED_MODAL_VIEWED);
    };

    const closeOpenWindowModal = () => {
        setOpenWindowOnboardingModalOpen(false);
        markOpenWindowOnboardingShown();
    };

    const closeClosedWindowModal = () => {
        setClosedWindowOnboardingModalOpen(false);
        markClosedWindowOnboardingShown();
    };

    useEffect(() => {
        if (marketplaceOpen === undefined || isFeatureRecordLoading) return;
        if (account?.customerID && isSubscriptionLoading) return;
        if (!onboardingEligible) return;

        if (marketplaceOpen && shouldShowOpenWindowOnboarding()) {
            setOpenWindowOnboardingModalOpen(true);
        } else if (!marketplaceOpen && shouldShowClosedWindowOnboarding()) {
            setClosedWindowOnboardingModalOpen(true);
        }
    }, [
        marketplaceOpen,
        isFeatureRecordLoading,
        isSubscriptionLoading,
        onboardingEligible,
        account?.customerID,
        featureRecord,
        setClosedWindowOnboardingModalOpen,
        setOpenWindowOnboardingModalOpen,
    ]);

    return {
        closeClosedWindowModal,
        closeOpenWindowModal,
        markClosedWindowOnboardingShown,
        markOpenWindowOnboardingShown,
        shouldShowClosedWindowOnboarding,
        shouldShowOpenWindowOnboarding,
    };
}

export default useOnboardingModals;
